import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ProjectTask } from '@app/manage/models/project-task';
import {
  EARLY_MONTH_THRESHOLD,
  TRAINING_DESCRIPTION_MIN_LENGTH,
  TRAINING_TASK_NAME,
} from '@app/shared/constants';
import { TimeSheetUpdate } from '@app/time-sheet/models/time-sheet-update';
import { AbsencesDaysService } from '@app/time-sheet/service/absences-days-service';
import { TimeSheetChangeTrackerService } from '@app/time-sheet/service/time-sheet-change-tracker-service';
import { TimeSheetService } from '@app/time-sheet/service/time-sheet-service';
import { timeToTimeInputFormat } from '@app/time-sheet/util/time-sheet-utils';
import { AuthService } from '@auth/auth-service';
import { ConfirmModal } from '@components/confirm-modal/confirm-modal';
import {
  NgbDropdown,
  NgbDropdownItem,
  NgbDropdownMenu,
  NgbDropdownToggle,
} from '@ng-bootstrap/ng-bootstrap';

import { areIntervalsOverlapping, getDate, getDaysInMonth, isAfter, isSameMonth } from 'date-fns';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs';

import { TimeSheetTask } from '../../models/time-sheet-task';
import { taskVisibleInterval } from '../../util/time-sheet-date-utils';

@Component({
  selector: 'abc-time-sheet-action-buttons',
  imports: [
    ConfirmModal,
    DatePipe,
    FormsModule,
    NgbDropdown,
    NgbDropdownToggle,
    NgbDropdownMenu,
    NgbDropdownItem,
  ],
  templateUrl: 'time-sheet-action-buttons.html',
  styleUrl: 'time-sheet-action-buttons.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimeSheetActionButtons {
  timeSheetChangeTrackerService = inject(TimeSheetChangeTrackerService);
  absencesDaysService = inject(AbsencesDaysService);
  private timeSheetService = inject(TimeSheetService);
  private toastrService = inject(ToastrService);
  private authService = inject(AuthService);

  year = input.required<number>();
  month = input.required<number>();
  tasks = input.required<TimeSheetTask[] | null>();

  save = output<void>();
  generate = output<void>();
  complete = output<void>();

  pendingChanges = this.timeSheetChangeTrackerService.pendingChanges;
  absenceDays = this.absencesDaysService.absenceDays;
  protected isFreelancer = this.authService.isFreelancer;

  isGenerationPossible = computed(() => {
    const tasks = this.tasks();
    if (!tasks) return false;
    return tasks.every((t) => !t.completed);
  });

  isEveryTaskCompleted = computed(() => {
    const tasks = this.tasks();
    if (!tasks) return false;
    return tasks.every((t) => t.completed);
  });

  private productiveTasks = computed(() => {
    return (this.tasks() ?? []).filter((task) => task.isProductive);
  });

  hasMultipleOverlappingProductiveTasks = computed(() => {
    const tasks = this.productiveTasks();
    if (tasks.length <= 1) return false;

    const intervals = tasks.map((task) => taskVisibleInterval(task));
    for (let i = 0; i < intervals.length; i++) {
      for (let j = i + 1; j < intervals.length; j++) {
        if (areIntervalsOverlapping(intervals[i], intervals[j], { inclusive: true })) {
          return true;
        }
      }
    }
    return false;
  });

  multipleProductiveTasks = computed(() => {
    const tasks = this.productiveTasks();

    if (tasks.length <= 1) return [];
    return tasks.map(
      (task) =>
        ({
          id: task.projectId,
          taskName: task.taskName,
          customerName: task.customerName,
        }) as ProjectTask
    );
  });

  allTrainingsHaveDescriptions = computed(() => {
    const changes: TimeSheetUpdate[] = this.timeSheetChangeTrackerService.changes();
    const trainingChanges: TimeSheetUpdate[] = changes.filter(
      (change) => change.taskName === TRAINING_TASK_NAME
    );
    return trainingChanges.every((change) => {
      if (!change.totalHours || change.totalHours === '00:00') {
        return true;
      }
      return (
        change.description !== null &&
        change.description.trim().length >= TRAINING_DESCRIPTION_MIN_LENGTH
      );
    });
  });

  canSave = computed(() => {
    return this.pendingChanges() && this.allTrainingsHaveDescriptions();
  });

  isEarlyCurrentMonth = computed(() => {
    const now = new Date();
    const targetDate = new Date(this.year(), this.month() - 1, 1);

    if (!isSameMonth(now, targetDate)) {
      return false;
    }

    const daysInMonth = getDaysInMonth(now);
    const threshold = Math.floor(daysInMonth * EARLY_MONTH_THRESHOLD);

    return getDate(now) <= threshold;
  });

  isFutureMonth = computed(() => {
    const targetDate = new Date(this.year(), this.month() - 1, 1);
    return isAfter(targetDate, new Date());
  });

  isAdvancedOpen = signal(false);

  constructor() {
    effect(() => {
      if (this.hasMultipleOverlappingProductiveTasks()) {
        this.isAdvancedOpen.set(true);
      }
    });
  }

  toggleIsAdvancedOpen() {
    this.isAdvancedOpen.set(!this.isAdvancedOpen());
  }

  getMonthTotal(): { productive: string; nonProductive: string } {
    const tasks = this.tasks();
    if (!tasks)
      return {
        productive: timeToTimeInputFormat(0, 0),
        nonProductive: timeToTimeInputFormat(0, 0),
      };

    let totalProductiveMinutes = 0;
    let totalNonProductiveMinutes = 0;

    tasks.forEach((t) => {
      t.days?.forEach((d) => {
        const [valueHours, valueMinutes] = d.totalHours.split(':').map(Number);
        if (t.isProductive) {
          totalProductiveMinutes += valueHours * 60 + valueMinutes;
        } else {
          totalNonProductiveMinutes += valueHours * 60 + valueMinutes;
        }
      });
    });

    return {
      productive: timeToTimeInputFormat(
        Math.floor(totalProductiveMinutes / 60),
        totalProductiveMinutes % 60
      ),
      nonProductive: timeToTimeInputFormat(
        Math.floor(totalNonProductiveMinutes / 60),
        totalNonProductiveMinutes % 60
      ),
    };
  }

  getAbsences() {
    this.absencesDaysService.refreshVacationDays(this.year(), this.month());
  }

  selectedPrimaryProject = signal<string | null>(null);
  canConfirmGeneration = computed(() => {
    if (this.absencesDaysService.isLoading()) {
      return false;
    }

    if (this.hasMultipleOverlappingProductiveTasks() && !this.selectedPrimaryProject()) {
      return false;
    }

    return true;
  });

  isGeneratingTimesheet = signal<boolean>(false);
  generateTimeSheet(options?: { primaryProject?: string | null }) {
    this.isGeneratingTimesheet.set(true);
    this.generate.emit();

    this.timeSheetService
      .generateTimeSheet(this.year(), this.month(), options)
      .pipe(finalize(() => this.isGeneratingTimesheet.set(false)))
      .subscribe();
  }

  syncAbsences() {
    this.isGeneratingTimesheet.set(true);
    this.generate.emit();

    this.absencesDaysService
      .generateAbsencesTimeSheet(this.year(), this.month())
      .pipe(finalize(() => this.isGeneratingTimesheet.set(false)))
      .subscribe();
  }

  isCompletingTimesheet = signal<boolean>(false);
  completeTimesheet() {
    this.isCompletingTimesheet.set(true);
    this.complete.emit();

    this.timeSheetService
      .completeTimeSheet(this.year(), this.month())
      .pipe(finalize(() => this.isCompletingTimesheet.set(false)))
      .subscribe({
        next: () =>
          this.toastrService.success(
            $localize`:toast|Success message for completing timesheet@@completeTimesheetSuccess:Timesheet completed successfully.`
          ),
        error: () =>
          this.toastrService.error(
            $localize`:toast|Error message for failed timesheet completion@@completeTimesheetError:Failed to complete timesheet.`
          ),
      });
  }
}
